import React, { useRef, useState, useEffect } from "react";
import "../../assets/scss/pages/ddt.scss";
import moment from "moment";
import { List } from "../../Components/common/List/List";
import { useAppContext } from "../../AppProvider";
import {
  ListFooter,
  ListHeader,
  ListItem,
  useListProps,
} from "./utilities/list";
import { ButtonConfirm } from "../../Components/common/Buttons/ButtonConfirm";
import LogoDark from "../../assets/images/logo-black.svg";
import { useReactToPrint } from "react-to-print";
import { useDDTContext } from "./providers/Index";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLoader } from "@awesome.me/kit-c1b85ff10f/icons/classic/light";
import { ToastContainer } from "react-toastify";

const DDTShipment = () => {
  const { dictionary } = useAppContext();
  const listProps = useListProps();

  const listRef = useRef(null);
  const contentRef = useRef(null);
  const context = useDDTContext();

  const [isPrint, setIsPrint] = useState(false);

  const renderHeader = () => <ListHeader isPrint={isPrint} />;
  const renderItem = (item, key) => (
    <ListItem
      item={{ ...item, currency: context?.shipment?.order?.currency }}
      index={key}
      isPrint={isPrint}
    />
  );
  const renderFooter = () => (
    <ListFooter data={context.shipment?.order} isPrint={isPrint} />
  );

  const printFn = useReactToPrint({
    contentRef: contentRef,
    onAfterPrint: () => {
      setIsPrint(false);
    },
    documentTitle: `${context?.shipment?.carrierReference}_DDT`,
  });

  const handleOnClick = React.useCallback(() => {
    setIsPrint(true);
  });

  useEffect(() => {
    if (isPrint) {
      setTimeout(() => {
        printFn();
      }, 200);
    }
  }, [isPrint]);

  useEffect(() => {
    document.body.classList.add("overflow-auto");
    return () => document.body.classList.remove("overflow-auto");
  }, []);

  return (
    <>
      <div className="ddt" ref={contentRef}>
        <div className="ddt-header">
          <div className="cover">
            <div>
              <img src={LogoDark} width={"150px"} />
            </div>
            {context.print && (
              <ButtonConfirm icon={false} onClick={handleOnClick}>
                {dictionary.actions.print}
              </ButtonConfirm>
            )}
          </div>
        </div>
        <div className="ddt-content">
          {context?.shipmentLoader ? (
            <div className="text-center">
              <FontAwesomeIcon icon={faLoader} spin size="2x" />
            </div>
          ) : (
            <>
              <div className="row m-0">
                <h2 className="fw-bold">
                  {dictionary.shipments.transport_document} #
                  {context?.shipment?.internalReference}
                </h2>
              </div>
              <div className="row m-0">
                <div
                  className={`${
                    isPrint ? "col-6 pe-1" : "col-12 col-md-6 pe-md-1"
                  } mb-2`}
                >
                  <div className="box-container border border-light h-100">
                    <div>
                      <label className="label-2 d-inline">
                        {dictionary.dates.creation_date}:
                      </label>{" "}
                      <label className="label-3 d-inline">
                        {moment(context?.shipment?.creationDate).format(
                          "DD/MM/YYYY HH:mm:ss"
                        )}
                      </label>
                    </div>
                    <div>
                      <label className="label-2 d-inline">
                        {dictionary.parcels.number_of_packages_2}:
                      </label>{" "}
                      <label className="label-3 d-inline">
                        {context?.shipment?.parcels}
                      </label>
                    </div>
                    <div>
                      <label className="label-2 d-inline">
                        {dictionary.dimensions.weight}:
                      </label>{" "}
                      <label className="label-3 d-inline">
                        {context?.shipment?.weight?.toFixed(2) || "-"} kg
                      </label>{" "}
                      <label className="label-2 d-inline">
                        {dictionary.dimensions.volume}:
                      </label>{" "}
                      <label className="label-3 d-inline">
                        {context?.shipment?.volume?.toFixed(3) || "-"} m3
                      </label>
                    </div>
                    <div>
                      <label className="label-2 d-inline">
                        {dictionary.words.causal}:
                      </label>{" "}
                      <label className="label-3 d-inline">
                        {dictionary.words.sale}
                      </label>
                    </div>
                  </div>
                </div>
                <div
                  className={`${
                    isPrint ? "col-6 ps-1" : "col-12 col-md-6 ps-md-1"
                  } mb-2`}
                >
                  <div className="box-container border border-light h-100">
                    <h3 className="mb-1">{dictionary.registries.carrier}</h3>
                    <div>
                      <label className="label-2 d-inline">
                        {dictionary.registries.name}:
                      </label>{" "}
                      <label className="label-3 d-inline">
                        {context?.shipment?.carrierName || "-"}
                      </label>
                    </div>
                    {context?.shipment?.carrierReference && (
                      <div>
                        <label className="label-2 d-inline">
                          {dictionary.references.reference}:
                        </label>{" "}
                        <label className="label-3 d-inline">
                          {context?.shipment?.carrierReference}
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="row m-0">
                <div
                  className={`${
                    isPrint ? "col-6 pe-1" : "col-12 col-md-6 pe-md-1"
                  } mb-2`}
                >
                  <div className="box-container border border-light h-100">
                    <h3 className="mb-1">{dictionary.registries.sender}</h3>
                    <div
                      className="d-grid grid-column align-items-center mt-2"
                      style={{ gridTemplateColumns: `1fr 5fr` }}
                    >
                      <div className="text-center">
                        {context?.shipment?.senderLogoDarkUrl && (
                          <img
                            height={30}
                            src={context.shipment.senderLogoDarkUrl}
                            alt="logo"
                          />
                        )}
                      </div>
                      <div>
                        <label className="label-2 d-inline">
                          {dictionary.registries.company_name}:
                        </label>{" "}
                        <label className="label-3 d-inline">
                          {context?.shipment?.senderName}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className={`${
                    isPrint ? "col-6 ps-1" : "col-12 col-md-6 ps-md-1"
                  } mb-2`}
                >
                  <div className="box-container border border-light h-100">
                    <h3 className="mb-1">{dictionary.registries.receiver}</h3>
                    <div>
                      <label className="label-2 d-inline">
                        {dictionary.registries.company_name}:
                      </label>{" "}
                      <label className="label-3 d-inline">
                        {context?.shipment?.deliveryName}
                      </label>
                    </div>
                    {context?.shipment?.deliveryContactName && (
                      <div>
                        <label className="label-2 d-inline">
                          {dictionary.registries.name}:
                        </label>{" "}
                        <label className="label-3 d-inline">
                          {context?.shipment?.deliveryContactName}
                        </label>
                      </div>
                    )}
                    <div>
                      <label className="label-2 d-inline">
                        {dictionary.locations.address}:
                      </label>{" "}
                      <label className="label-3 d-inline">
                        {context?.shipment?.deliveryLocationLabel || "-"}
                      </label>
                    </div>
                    {context?.shipment?.deliveryVatNumber && (
                      <div>
                        <label className="label-2 d-inline">
                          {dictionary.registries.vat_number}:
                        </label>{" "}
                        <label className="label-3 d-inline">
                          {context?.shipment?.deliveryVatNumber}
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {context?.shipment?.order?.orderDetails && (
                <div className="row m-0">
                  {/* <ListProvider
                    key={"list-provider"}
                    name={listProps.name}
                    path={listProps.path}
                    defaultPagination={listProps.pagination}
                    parentId={context?.shipment?.orderId}
                    sorting={listProps.sorting}
                    callback={() => {
                      if (context?.print) {
                        setTimeout(() => {
                          printFn();
                        }, 500);
                      }
                    }}
                  > */}
                  <List
                    ref={listRef}
                    data={context.shipment?.order?.orderDetails || []}
                    title={listProps.title}
                    pagination={listProps.pagination}
                    selectRowsEnabled={listProps.selectRowsEnabled}
                    renderHeader={renderHeader}
                    renderItem={renderItem}
                    renderFooter={renderFooter}
                    actionBar={listProps.actionBar}
                  />
                  {/* </ListProvider> */}
                </div>
              )}
            </>
          )}
        </div>
        <div className="ddt-footer">
          <img src={LogoDark} alt="logo-footer" />
        </div>
        <ToastContainer />
      </div>
    </>
  );
};

export default DDTShipment;
